
import {InvalidArgumentException, IllegalStateException} from "../../errors/Exceptions.js";
import {UrmCommandType} from "./UrmCommand.js";
import {UrmMemento} from "./UrmMemento";


export class UnlimitedRegisterMachine {
    #registers; // number[]
    #executedCommandsCount = 0;
    #nextCommandIndex = 1; // index of command to be executed at the next step
    #isFinished = false;
    #program; // UrmProgram


    /**
     *
     * @param program {UrmProgram} is valid non-empty <code>UrmProgram</code> object (validated by its constructor)
     * @param initialRegisters {number[]} is array of initial values of registers - integer non-negative values.
     * Array may have empty elements. Not mentioned registers will be considered 0.
     * If number of registers will be bigger that mentioned by program, they **won't be** ignored
     */
    constructor(program, initialRegisters) {
        if(!Array.isArray(initialRegisters)){
            throw new InvalidArgumentException("initialRegisters must be an Array");
        }

        // init registers
        let numberOfRequiredRegisters = Math.max(program.getMaximumMentionedRegisterIndex() + 1, initialRegisters.length);
        this.#registers = Array(numberOfRequiredRegisters).fill(0);
        this.#initRegisters(initialRegisters);

        // init program
        this.#program = program;
    }


    /**
     * Initializes registers values. Before calling this function,
     * registers array must be already allocated (should not be empty space) and initialized by 0.
     * @param initialRegisters {number[]} is initial value of registers (array of integer non-negative values (may have empty elements)).
     * Not mentioned registers will be considered 0.
     */
    #initRegisters(initialRegisters){
        initialRegisters.forEach((registerValue, index, arr) => {
            if(!Number.isInteger(registerValue)){
                throw new InvalidArgumentException("Register value must be integer number");
            }
            if(registerValue < 0){
                throw new InvalidArgumentException("Register value must be non-negative");
            }

            this.#registers[index] = registerValue;
        });
    }

    get registers() {
        return this.#registers;
    }

    get executedCommandsCount() {
        return this.#executedCommandsCount;
    }

    get nextCommandIndex(){
        return this.#nextCommandIndex;
    }

    get isFinished() {
        return this.#isFinished;
    }

    get program() {
        return this.#program;
    }


    /**
     * Interprets and executes a command by given index, edit registers according to command,
     * **BUT doesn't edit** any other state of object (#executedCommandsCount, #isFinished, etc.).
     * @param commandIndex {number} is index of command to execute (must be >= 1)
     * @return {number} index of the next command. If it is bigger than index of last command in program, need to stop URM.
     */
    #executeCommandImpl(commandIndex){
        let command = this.#program.getCommandByIndex(commandIndex);
        switch(command.type){
            case UrmCommandType.Z:
                this.#registers[command.args[0]] = 0;
                return commandIndex + 1;
            case UrmCommandType.S:
                this.#registers[command.args[0]] += 1;
                return commandIndex + 1;
            case UrmCommandType.T:
                this.#registers[command.args[1]] = this.#registers[command.args[0]];
                return commandIndex + 1;
            case UrmCommandType.J:
                if(this.#registers[command.args[1]] === this.#registers[command.args[0]]){
                    return command.args[2];
                }
                return commandIndex + 1;
        }
    }

    /**
     * Executes next command if URM is not finished, throws IllegalStateException otherwise.
     */
    executeNextCommand(){
        if(this.#isFinished){
            throw new IllegalStateException("Can't execute next command when URM is already finished");
        }

        this.#nextCommandIndex = this.#executeCommandImpl(this.#nextCommandIndex);
        this.#executedCommandsCount++;

        // URM should stop, when next command does not exist
        // (index of the next command is bigger than index of the last command in program)
        if(this.#nextCommandIndex > this.#program.lastCommandIndex){
            this.#isFinished = true;
        }
    }


    /**
     * If not finished, executes rest of commands (until program ends, or reaches <code>limitOfSteps</code>)
     * @param limitOfSteps {number} is maximum number of commands allowed to be executed by this method.
     * It is recommended to set <code>limitOfSteps</code> to avoid infinite calculations. Must be positive number.
     */
    executeRestOfCommands(limitOfSteps){
        if(limitOfSteps !== undefined && limitOfSteps !== null && limitOfSteps < 1){
            throw new InvalidArgumentException("limitOfSteps must be positive");
        }

        if(this.#isFinished){
            throw new IllegalStateException("Can't execute rest of commands when URM is already finished");
        }

        let steps = 0;
        while(!this.#isFinished && (!limitOfSteps || steps < limitOfSteps)){
            this.executeNextCommand();
            steps++;
        }
    }


    /**
     * Executes full program on current (not initial) registers
     * @param limitOfSteps {number} is maximum number of commands allowed to be executed by this method.
     * It is recommended to set <code>limitOfSteps</code> to avoid infinite calculations. Must be positive number.
     */
    executeFullProgram(limitOfSteps){
        if(limitOfSteps !== undefined && limitOfSteps !== null && limitOfSteps < 1){
            throw new InvalidArgumentException("limitOfSteps must be positive");
        }

        this.#executedCommandsCount = 0;
        this.#nextCommandIndex = 1;
        this.#isFinished = false;

        let steps = 0;
        while(!this.#isFinished && (!limitOfSteps || steps < limitOfSteps)){
            this.executeNextCommand();
            steps++;
        }
    }


    /**
     * Resets URM and sets new values of registers
     * @param initialRegisters {number[]} is array of initial values of registers - integer non-negative values.
     * Array may have empty elements. Not mentioned registers will be considered 0.
     * If number of registers will be bigger that mentioned by program, they **won't be** ignored     */
    reset(initialRegisters){
        if(!Array.isArray(initialRegisters)){
            throw new InvalidArgumentException("initialRegisters must be an Array");
        }
        
        // init registers
        let numberOfRequiredRegisters = Math.max(
            this.#program.getMaximumMentionedRegisterIndex() + 1, initialRegisters.length);
        this.#registers = Array(numberOfRequiredRegisters).fill(0);
        this.#initRegisters(initialRegisters);

        // reset other fields
        this.#executedCommandsCount = 0;
        this.#nextCommandIndex = 1;
        this.#isFinished = false;
    }

    /**
     * Creates a copy of its state
     * @return {UrmMemento}
     */
    saveState(){
        return new UrmMemento(
            [...this.registers],
            this.executedCommandsCount,
            this.nextCommandIndex,
            this.isFinished
        );
    }

    /**
     * Sets the current URM state to the given <code>state</code>.
     *
     * @param state {UrmMemento} must be compatible with program in this URM object
     */
    restoreState(state){
        this.#registers = [...state.registers];
        this.#executedCommandsCount = state.executedCommandsCount;
        this.#nextCommandIndex = state.nextCommandIndex;
        this.#isFinished = state.isFinished;
    }


    toString(){
        let str = "";
        str += `Registers:\t\t [${this.#registers.toString()}] (${this.#registers.length} registers)\n`;
        str += `Executed commands:\t ${this.#executedCommandsCount}\n`;
        str += `Next command:\t\t ${this.#nextCommandIndex}\n`;
        str += `Is finished:\t\t ${this.#isFinished}\n`;
        str = str.slice(0, -1); // delete last \n
        return str;
    }


    // UrmProgramExecutor, UrmProgramBuilder is possible
}